import React, { useMemo } from 'react';
import {
  ChevronRight,
  Users,
  Package,
  HardDrive,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Building2,
  Layers,
} from 'lucide-react';
import { Card } from 'primereact/card';
import type { ResourceApprovalDetail, ProjectApprovalDetails } from '../../../services/projectApprovalService';

function statusBadge(status?: string) {
  if (status === 'approved') return <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">Approved</span>;
  if (status === 'rejected') return <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium">Rejected</span>;
  if (status === 'modified') return <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-medium">Modified</span>;
  return <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-medium">Pending</span>;
}

interface EquipmentItem {
  _id: string;
  equipmentCode: string;
  equipmentName: string;
  assetId?: string;
  assetName?: string;
  status: string;
}

interface AssetGroup {
  assetName: string;
  assetId: string;
  equipment: EquipmentItem[];
}

function groupEquipmentByAsset(equipmentList: EquipmentItem[]): AssetGroup[] {
  const groups: Record<string, AssetGroup> = {};
  equipmentList.forEach((eq) => {
    const assetName = eq.assetName ?? 'Unassigned Asset';
    const key = eq.assetId ?? assetName;
    if (!groups[key]) {
      groups[key] = { assetName, assetId: key, equipment: [] };
    }
    groups[key].equipment.push(eq);
  });
  return Object.values(groups);
}

export interface ResourceTreeViewProps {
  resources: ProjectApprovalDetails['resources'];
  projectId: string;
  onResourceApprove: (
    resource: ResourceApprovalDetail,
    resourceName: string,
    resourceType?: 'engineer' | 'supervisor',
    supervisorId?: string
  ) => void;
  onResourceReject: (
    resource: ResourceApprovalDetail,
    resourceName: string,
    resourceType?: 'engineer' | 'supervisor',
    supervisorId?: string,
    engineerIds?: string[]
  ) => void;
  actionLoading: boolean;
  onSyncAssignments: () => void;
  syncLoading: boolean;
  isReviewSubmitted?: boolean;
  isResubmitted?: boolean;
  isHead?: boolean;
}

const DEPTH_STYLES: Record<number, string> = {
  0: 'shadow-2xl shadow-slate-300/40',
  1: 'shadow-xl shadow-slate-300/30',
  2: 'shadow-lg shadow-slate-200/25',
  3: 'shadow-md shadow-slate-200/20',
  4: 'shadow-sm shadow-slate-100/15',
};

export function ResourceTreeView({
  resources,
  projectId,
  onResourceApprove,
  onResourceReject,
  actionLoading,
  onSyncAssignments,
  syncLoading,
  isReviewSubmitted = false,
  isResubmitted = false,
  isHead = true,
}: ResourceTreeViewProps) {
  const totalEquipmentCount = useMemo(() => {
    let count = 0;
    (resources.hierarchy ?? []).forEach((sup) => {
      count += sup.allocatedEquipment ?? 0;
      sup.subordinates?.forEach((eng) => {
        count += eng.allocatedEquipment ?? 0;
      });
    });
    (resources.independentEngineers ?? []).forEach((eng) => {
      count += eng.allocatedEquipment ?? 0;
    });
    return count;
  }, [resources]);

  if (totalEquipmentCount === 0) {
    return (
      <div className="text-center py-10 bg-white rounded-2xl border-2 border-dashed border-slate-200 shadow-inner">
        <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500 font-medium">No resources with equipment</p>
        <button
          onClick={onSyncAssignments}
          disabled={syncLoading}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl text-sm font-semibold disabled:opacity-50 transition-all"
        >
          {syncLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Sync Assignments
        </button>
      </div>
    );
  }

  const statusStyles: Record<string, string> = {
    approved: 'approval-card border-[var(--color-success-300)] bg-gradient-to-br from-[var(--color-success-50)] to-white',
    rejected: 'approval-card border-[var(--color-error-300)] bg-gradient-to-br from-[var(--color-error-50)] to-white',
    pending: 'approval-card border-[var(--color-gray-200)] bg-white',
    modified: 'approval-card border-[var(--color-warning-400)] bg-gradient-to-br from-[var(--color-warning-50)] to-white',
  };

  const renderEquipment = (eq: EquipmentItem) => (
    <div
      key={eq._id}
      className="flex items-center gap-2 py-2 px-3 rounded-xl bg-white/90 border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all duration-200 group"
    >
      <div
        className={`w-2 h-2 rounded-full shrink-0 ${eq.status === 'active' ? 'bg-emerald-500 shadow-sm shadow-emerald-500/40' : 'bg-slate-300'}`}
      />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-slate-800 group-hover:text-indigo-700 transition-colors">
          {eq.equipmentName}
        </p>
        {eq.equipmentCode && (
          <p className="text-[10px] text-slate-400 font-mono mt-0.5">{eq.equipmentCode}</p>
        )}
      </div>
    </div>
  );

  const renderAssetNode = (
    assetKey: string,
    assetName: string,
    equipment: EquipmentItem[],
    resourceId: string,
    depth: number
  ) => {
    const key = `${resourceId}::${assetKey}`;
    return (
      <div key={key} className="relative pl-6 mt-2">
        <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-indigo-200/80 to-transparent" />
        <div
          className={`w-full text-left rounded-xl border-2 transition-all duration-300 overflow-hidden ${DEPTH_STYLES[Math.min(depth, 4)]} bg-gradient-to-br from-slate-50 to-white border-slate-200`}
        >
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 rotate-90">
              <ChevronRight className="w-4 h-4" />
            </div>
            <Package className="w-5 h-5 text-indigo-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-800 truncate">{assetName}</p>
              <p className="text-[10px] text-slate-500">{equipment.length} equipment</p>
            </div>
            <span className="px-2 py-0.5 rounded-lg bg-slate-200/80 text-slate-600 text-[10px] font-bold">
              {equipment.length}
            </span>
          </div>
        </div>
        <div className="mt-2 pl-4 space-y-1.5">
          {equipment.map((eq) => renderEquipment(eq))}
        </div>
      </div>
    );
  };

  const renderResourceNode = (
    resource: ResourceApprovalDetail,
    type: 'supervisor' | 'engineer',
    supervisorId?: string,
    depth: number = 0
  ) => {
    const isSupervisor = type === 'supervisor';
    const equipmentList = resource.equipment ?? [];
    const assetGroups = groupEquipmentByAsset(equipmentList);
    const subordinates = resource.subordinates ?? [];
    const subordinatesWithEquipment = subordinates.filter((eng) => (eng.allocatedEquipment ?? 0) > 0);
    const hasChildren =
      (isSupervisor && (subordinatesWithEquipment.length > 0 || equipmentList.length > 0)) ||
      (!isSupervisor && (assetGroups.length > 0 || equipmentList.length > 0));
    const approvalStatus = resource.approvalStatus ?? 'pending';

    return (
      <div key={resource._id} className="relative pl-6 mt-3">
        <div
          className="absolute left-0 top-0 bottom-0 w-0.5 rounded-full bg-gradient-to-b from-indigo-300/70 via-cyan-300/50 to-transparent"
          style={{ minHeight: 24 }}
        />
        <div className={`rounded-xl overflow-hidden bg-white ${statusStyles[approvalStatus] ?? statusStyles.pending}`}>
          {/* Header: avatar, name, role, status */}
          <div className="flex items-center gap-3 p-3 border-b border-[var(--color-gray-100)]">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm shrink-0"
              style={{
                background: isSupervisor
                  ? 'linear-gradient(135deg, var(--color-blue-light-700) 0%, var(--color-blue-light-600) 100%)'
                  : 'linear-gradient(135deg, var(--color-green-600) 0%, var(--color-primary) 100%)',
                boxShadow: 'var(--shadow-theme-xs)',
              }}
            >
              {resource.name.charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-900">{resource.name}</span>
                <span
                  className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${isSupervisor ? 'bg-indigo-100 text-indigo-700' : 'bg-cyan-100 text-cyan-700'}`}
                >
                  {isSupervisor ? 'Supervisor' : 'Engineer'}
                </span>
                {statusBadge(approvalStatus)}
              </div>
              <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                <span className="flex items-center gap-1">
                  <HardDrive size={11} className="text-slate-400" />
                  <span className="font-medium text-slate-600">{resource.allocatedEquipment ?? 0}</span> equipment
                </span>
                <span className="flex items-center gap-1">
                  <Package size={11} className="text-slate-400" />
                  <span className="font-medium text-slate-600">{assetGroups.length}</span> assets
                </span>
                {isSupervisor && subordinatesWithEquipment.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Users size={11} className="text-slate-400" />
                    <span className="font-medium text-slate-600">{subordinatesWithEquipment.length}</span> engineers
                  </span>
                )}
              </div>
              {resource.email && (
                <p className="text-[10px] text-slate-400 mt-0.5 truncate" title={resource.email}>{resource.email}</p>
              )}
            </div>
          </div>

          {/* Decision: labeled Approve / Reject — both buttons always shown for approvers */}
          {isHead && !isReviewSubmitted && (
            <div className="px-3 py-2 bg-[var(--color-gray-50)] border-t border-[var(--color-gray-200)] flex items-center justify-between gap-3" onClick={(e) => e.stopPropagation()}>
              <span className="text-[10px] font-semibold text-[var(--color-gray-500)] uppercase tracking-wider">Assignment approval</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    onResourceApprove(resource, resource.name, isSupervisor ? 'supervisor' : 'engineer', supervisorId)
                  }
                  disabled={actionLoading}
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-semibold text-sm min-w-[88px] approve-btn-gradient disabled:opacity-50 ${approvalStatus === 'approved' ? 'ring-2 ring-emerald-400' : ''}`}
                  title={approvalStatus === 'approved' ? 'Approved — click to re-approve' : 'Approve this assignment'}
                >
                  <CheckCircle size={14} />
                  <span>{approvalStatus === 'approved' ? 'Approved' : 'Approve'}</span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onResourceReject(
                      resource,
                      resource.name,
                      isSupervisor ? 'supervisor' : 'engineer',
                      supervisorId,
                      isSupervisor ? subordinatesWithEquipment.map((eng) => eng._id) : undefined
                    )
                  }
                  disabled={actionLoading}
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-semibold text-sm min-w-[88px] reject-btn-outline disabled:opacity-50 ${approvalStatus === 'rejected' ? 'ring-2 ring-red-400' : ''}`}
                  title={approvalStatus === 'rejected' ? 'Rejected — click to re-reject' : 'Reject this assignment'}
                >
                  <XCircle size={14} />
                  <span>{approvalStatus === 'rejected' ? 'Rejected' : 'Reject'}</span>
                </button>
              </div>
            </div>
          )}

          {approvalStatus === 'rejected' && resource.approverComments && !isResubmitted && (
            <div className="px-3 pb-3">
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-800 flex gap-2 items-start">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <div>
                  <span className="font-semibold block mb-0.5">Rejection Reason:</span>
                  {resource.approverComments}
                </div>
              </div>
            </div>
          )}

          {isResubmitted && approvalStatus === 'pending' && resource.approverComments && (
            <div className="px-3 pb-3">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                Previous rejection: {resource.approverComments}
              </div>
            </div>
          )}

          {(assetGroups.length > 0 || (isSupervisor && subordinatesWithEquipment.length > 0)) && (
            <div className="px-3 pb-3 pt-2 border-t border-slate-100 space-y-3">
              {assetGroups.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-4 h-4 text-indigo-500" />
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      Assets &amp; Equipment
                    </span>
                  </div>
                  <div className="space-y-2">
                    {assetGroups.map((group) =>
                      renderAssetNode(
                        group.assetId,
                        group.assetName,
                        group.equipment,
                        resource._id,
                        depth + 1
                      )
                    )}
                  </div>
                </div>
              )}
              {isSupervisor && subordinatesWithEquipment.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-cyan-500" />
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      Managed Engineers
                    </span>
                  </div>
                  <div className="space-y-2">
                    {subordinatesWithEquipment.map((eng) => renderResourceNode(eng, 'engineer', resource._id, depth + 1))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const totalResources =
    (resources.hierarchy?.length ?? 0) + (resources.independentEngineers?.length ?? 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card className="!shadow-sm !border !border-slate-200 !rounded-xl overflow-hidden">
          <div className="p-2.5 flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total Resources</p>
              <p className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{totalResources}</p>
            </div>
            <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Users size={18} className="text-indigo-600" />
            </div>
          </div>
        </Card>
        <Card className="!shadow-sm !border !border-slate-200 !rounded-xl overflow-hidden">
          <div className="p-2.5 flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total Equipment</p>
              <p className="text-lg font-bold text-slate-900 tabular-nums mt-0.5">{totalEquipmentCount}</p>
            </div>
            <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <HardDrive size={18} className="text-emerald-600" />
            </div>
          </div>
        </Card>
        <div className="col-span-2 flex items-stretch">
          <Card className="!shadow-sm !border !border-slate-200 !rounded-xl overflow-hidden flex-1">
            <div className="p-2.5 flex flex-col justify-center">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Actions</p>
              <button
                onClick={onSyncAssignments}
                disabled={syncLoading}
                className="flex items-center justify-center gap-2 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-600 text-[11px] font-semibold rounded-lg border border-slate-200 transition-colors disabled:opacity-50"
              >
                {syncLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Sync Assignments
              </button>
            </div>
          </Card>
        </div>
      </div>

      <Card className="!shadow-sm !border !border-slate-200 !rounded-xl overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-800">Resource hierarchy</h2>
            <p className="text-[11px] text-slate-500">Supervisors, engineers, assets and equipment</p>
          </div>
        </div>
        <div className="p-3 space-y-1">
          {(resources.hierarchy ?? []).map((sup) => renderResourceNode(sup, 'supervisor', undefined, 0))}
          {(resources.independentEngineers ?? []).length > 0 && (
            <div className="pt-4 mt-4 border-t border-dashed border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-slate-500" />
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Independent Engineers</span>
                <span className="text-[10px] text-slate-500 tabular-nums">({resources.independentEngineers?.length ?? 0})</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="space-y-1">
                {(resources.independentEngineers ?? []).map((eng) =>
                  renderResourceNode(eng, 'engineer', undefined, 0)
                )}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
